import { Popconfirm, Space, Typography } from 'antd';
import { getI18n, useTranslation } from 'react-i18next';
import { grispiSubscriptionModal, subscriptionFeatureMap } from '../../containers/subscription/SubscriptionController';
import { IMacro } from '../../hooks/macrosPage/useMacroList';
import { deleteMacro, disableMacro, enableMacro } from '../../services/admin/macros';
import { grispiNotification } from '../../store/useNotificationStore';

const { Link } = Typography;

const ENABLE_MACRO = 'ENABLE_MACRO';
const DISABLE_MACRO = 'DISABLE_MACRO';
const DELETE_MACRO = 'DELETE_MACRO';

type ActionFunction = (macro: IMacro, callback: () => Promise<void>) => Promise<void>;

export const tryCatch = async (callback: any, key: string) => {
    try {
        await callback();
    } catch (error) {
        const limitKey = error?.response?.data?.limitKey;
        const feature = Object.keys(subscriptionFeatureMap).find(
            (feature) => typeof limitKey === 'string' && limitKey.includes(feature)
        );
        if (feature) {
            grispiNotification('destroy', '', key);
            grispiSubscriptionModal({ feature: subscriptionFeatureMap[feature] });
            return;
        }
        if (error?.response?.statusText === 'EXPORT_JOB_ALREADY_RUNNING')
            grispiNotification('error', getI18n().t('exportFilter.exportMessage.exportError', { ns: 'admin' }), key);

        if (error?.response?.statusText === 'FIELD_DEFINITION_NOT_FOUND') {
            const fieldKey = error?.response?.data?.message?.split("'")[1];
            grispiNotification('error', getI18n().t('errors.fieldNotFound', { ns: 'admin', fieldKey }), key);
        } else grispiNotification('error', limitKey, key);
    }
};

const enableMacroAction: ActionFunction = async (macro, callback) => {
    grispiNotification('loading', getI18n().t('enabling', { ns: 'admin' }), ENABLE_MACRO);

    tryCatch(async () => {
        await enableMacro(macro.id);
        grispiNotification('success', getI18n().t('enabled', { ns: 'admin' }), ENABLE_MACRO);
        await callback();
    }, ENABLE_MACRO);
};

const disableMacroAction: ActionFunction = async (macro, callback) => {
    grispiNotification('loading', getI18n().t('disabling', { ns: 'admin' }), DISABLE_MACRO);

    tryCatch(async () => {
        await disableMacro(macro.id);
        grispiNotification('success', getI18n().t('disabled', { ns: 'admin' }), DISABLE_MACRO);
        await callback();
    }, DISABLE_MACRO);
};

const deleteMacroAction: ActionFunction = async (macro, callback) => {
    grispiNotification('loading', getI18n().t('deleting', { ns: 'admin' }), DELETE_MACRO);

    tryCatch(async () => {
        await deleteMacro(macro.id);
        grispiNotification('success', getI18n().t('deleted', { ns: 'admin' }), DELETE_MACRO);
        await callback();
    }, DELETE_MACRO);
};

const MacroActionButton = ({ macro, callback }: { macro: IMacro; callback: () => any }): JSX.Element => {
    const { t } = useTranslation('admin');

    return (
        <>
            <Space size="middle">
                {macro.enabled ? (
                    <Popconfirm title={t('sureToDisable')} onConfirm={() => disableMacroAction(macro, callback)}>
                        <Link onClick={(event) => event.stopPropagation()}>{t('ticketField.disable')}</Link>
                    </Popconfirm>
                ) : (
                    <Popconfirm title={t('sureToEnable')} onConfirm={() => enableMacroAction(macro, callback)}>
                        <Link onClick={(event) => event.stopPropagation()}>{t('ticketField.enable')}</Link>
                    </Popconfirm>
                )}

                {!macro.enabled && (
                    <Popconfirm title={t('sureToDelete')} onConfirm={() => deleteMacroAction(macro, callback)}>
                        <Link>{t('delete')}</Link>
                    </Popconfirm>
                )}
            </Space>
        </>
    );
};

export default MacroActionButton;
